import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

/**
 * The docked side panel: a record opened beside the page instead of on top of it. A layout drops
 * one `LayoutPanelSlot` next to its scrolling content; `PanelFrame` portals a panel into it from
 * anywhere inside the layout. The panel takes its width out of the content area, so nothing is
 * covered and the page behind stays fully usable: no backdrop, no focus trap.
 *
 * Features never use this file directly. They render `PanelShell` from
 * `@/components/layouts/shells/panel-shell`, which gives the frame the installed layout's look.
 *
 * Like `layout-slots.tsx`, this file is identical across every layout pack (enforced by
 * `layout-slots.sync.test.ts`).
 */
export const LAYOUT_PANEL_SLOT_ID = "layout-panel-slot";
const SLOT_CHANGED_EVENT = "layout:panel-slot-changed";
const WIDTH_STORAGE_KEY = "layout:panel-width";

export const PANEL_DEFAULT_WIDTH = 460;
export const PANEL_MIN_WIDTH = 360;
export const PANEL_MAX_WIDTH = 960;
const KEYBOARD_STEP = 24;

function notifySlotChange() {
  queueMicrotask(() => window.dispatchEvent(new Event(SLOT_CHANGED_EVENT)));
}

/**
 * Where the panel docks. `display: contents` keeps the slot out of the layout: the panel itself
 * becomes the flex child, and an empty slot leaves no trace.
 */
export function LayoutPanelSlot() {
  return <div ref={notifySlotChange} id={LAYOUT_PANEL_SLOT_ID} className="contents" />;
}

/** The widest the panel may get: the fixed maximum, or less on a narrow window so the page keeps room. */
const maxWidthFor = (viewport: number) =>
  Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, Math.round(viewport * 0.7)));

export const clampPanelWidth = (width: number, viewport: number) =>
  Math.min(maxWidthFor(viewport), Math.max(PANEL_MIN_WIDTH, Math.round(width)));

const readStoredWidth = () => {
  try {
    const stored = Number(window.localStorage.getItem(WIDTH_STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : PANEL_DEFAULT_WIDTH;
  } catch {
    return PANEL_DEFAULT_WIDTH;
  }
};

const storeWidth = (width: number) => {
  try {
    window.localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
  } catch {
    // Private mode or blocked storage: the width simply does not survive a reload.
  }
};

/** True when a key press belongs to whatever has focus, not to the panel. */
const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
    target.closest('[role="dialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]') !== null);

export interface PanelFrameProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Id of the element that names the panel, for assistive tech. */
  labelledBy: string;
  /** Accessible name of the resize handle. */
  resizeLabel: string;
  children: ReactNode;
  className?: string;
  /** Classes for the visible line of the resize handle. */
  handleClassName?: string;
}

/**
 * The panel's behaviour, with no opinion on its looks: docking into the slot, drag and keyboard
 * resizing with a remembered width, Escape to close. Below the `md` breakpoint there is no room
 * to dock, so the panel covers the page like a screen of its own.
 */
export function PanelFrame({
  open,
  onOpenChange,
  labelledBy,
  resizeLabel,
  children,
  className,
  handleClassName,
}: PanelFrameProps) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState(PANEL_DEFAULT_WIDTH);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ startX: number; startWidth: number } | null>(null);
  const latestWidth = useRef(width);
  latestWidth.current = width;

  useEffect(() => {
    const updateSlot = () => setSlot(document.getElementById(LAYOUT_PANEL_SLOT_ID));
    updateSlot();
    window.addEventListener(SLOT_CHANGED_EVENT, updateSlot);
    return () => window.removeEventListener(SLOT_CHANGED_EVENT, updateSlot);
  }, []);

  // The stored width is read after mount (not during render) and re-clamped when the window shrinks.
  useEffect(() => {
    const fit = (next: number) => setWidth(clampPanelWidth(next, window.innerWidth));
    fit(readStoredWidth());
    const onResize = () => fit(latestWidth.current);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || isTypingTarget(event.target)) return;
      onOpenChange(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  const commit = useCallback((next: number) => {
    const clamped = clampPanelWidth(next, window.innerWidth);
    setWidth(clamped);
    storeWidth(clamped);
  }, []);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { startX: event.clientX, startWidth: width };
    setDragging(true);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    // The handle is on the panel's left edge: dragging left makes the panel wider.
    setWidth(clampPanelWidth(drag.current.startWidth + (drag.current.startX - event.clientX), window.innerWidth));
  };

  const endDrag = () => {
    if (!drag.current) return;
    drag.current = null;
    setDragging(false);
    storeWidth(latestWidth.current);
  };

  const onHandleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const steps: Record<string, number> = {
      ArrowLeft: width + KEYBOARD_STEP,
      ArrowRight: width - KEYBOARD_STEP,
      Home: PANEL_MAX_WIDTH,
      End: PANEL_MIN_WIDTH,
    };
    const next = steps[event.key];
    if (next === undefined) return;
    event.preventDefault();
    commit(next);
  };

  const style = useMemo(() => ({ "--layout-panel-width": `${width}px` }) as CSSProperties, [width]);
  const resetWidth = useCallback(() => commit(PANEL_DEFAULT_WIDTH), [commit]);

  if (!open || !slot) return null;

  return createPortal(
    <aside
      aria-labelledby={labelledBy}
      data-slot="layout-panel"
      data-dragging={dragging || undefined}
      style={style}
      className={cn(
        "relative flex min-h-0 flex-col max-md:fixed max-md:inset-0 max-md:z-40 md:w-(--layout-panel-width) md:shrink-0",
        dragging && "select-none",
        className,
      )}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={resizeLabel}
        aria-valuemin={PANEL_MIN_WIDTH}
        aria-valuemax={PANEL_MAX_WIDTH}
        aria-valuenow={width}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={resetWidth}
        onKeyDown={onHandleKeyDown}
        className="group/handle absolute inset-y-0 -left-1.5 z-10 hidden w-3 cursor-col-resize touch-none outline-none md:block"
      >
        <span
          className={cn(
            "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border transition-[width,background-color] duration-150 group-hover/handle:w-0.5 group-hover/handle:bg-primary/60 group-focus-visible/handle:w-0.5 group-focus-visible/handle:bg-primary",
            dragging && "w-0.5 bg-primary",
            handleClassName,
          )}
        />
      </div>
      {children}
    </aside>,
    slot,
  );
}
